import Vue from 'vue'
import auth0 from 'auth0-js'
import localStorageHelper from '@/helpers/localStorageHelper'

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname)

let instance

/** Returns the current instance of the SDK */
export const getInstance = () => instance

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
                           onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
                           redirectUri = window.location.origin + '/login-callback',
                           ...options
                         }) =>
{
  if (instance)
  {
    return instance
  }

  // The 'instance' is simply a Vue object
  instance = new Vue(
    {
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        webAuth: null,
        popupOpen: false,
        error: null,
        accessToken: null
      }
    },
    methods: {
      login (customState, provider = 'google-oauth2') {
        this.webAuth.authorize({
          connection: provider,
          appState: customState
        })
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o)
      {
        return this.auth0Client.loginWithRedirect(o)
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o)
      {
        return this.auth0Client.getIdTokenClaims(o)
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o)
      {
        return this.auth0Client.getTokenSilently(o)
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o)
      {
        return this.auth0Client.getTokenWithPopup(o)
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o)
      {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('removeAT')
        localStorage.removeItem('targetUrl')
        localStorage.removeItem('skipIdentity')
        localStorage.removeItem('userData')
        localStorage.removeItem('email')

        // clear cached filters and classificators
        localStorage.removeItem('filters')
        localStorage.removeItem('filtersUpdated')
        localStorage.removeItem('classificators')
        localStorage.removeItem('classificatorsUpdated')

        // clear additional elements
        // localStorage.removeItem('error')
        localStorage.removeItem('alreadyReloaded')
        localStorage.removeItem('profileName')

        window.location.href = '/'
        // return this.auth0Client.logout(o)
      }
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created()
    {
      // Create a new instance of the SDK client using members of the given options object
      this.webAuth = new auth0.WebAuth({
        domain: options.domain,
        clientID: options.clientId,
        audience: options.audience,
        redirectUri: window.location.origin + '/login-callback',
        responseType: 'token',
        scope: 'openid profile email'
      })

      // Initialize our internal authentication state
      if (localStorage.getItem('accessToken') !== null) {
        this.isAuthenticated = true
        // Additional checking expire time of token
        if (localStorage.getItem('removeAT') !== null) {
          // console.log('removeAT:', localStorage.getItem('removeAT'))
          // console.log('Date.now():', Date.now() / 1000)
          if (Math.round(Date.now() / 1000) > localStorage.getItem('removeAT'))
          {
            // console.log('REMOVE TOKEN! EXPIRED!!!!!')
            localStorage.removeItem('removeAT')
            localStorage.removeItem('accessToken')
            this.isAuthenticated = false
          }
        }
      } else {
        this.webAuth.parseHash((err, authResult) => {
          if (!err && authResult) {
            localStorage.setItem('removeAT', Math.round(Date.now() / 1000) + authResult.expiresIn)
            localStorage.setItem('accessToken', authResult.accessToken)
          }
          else if (err)
          {
            const loginProvider = localStorageHelper.get('loginProvider')
            if (loginProvider && err.errorDescription)
            {
              localStorageHelper.set('login_fail', err.errorDescription)
            }
              window.location.href = '/login'
          }
        })
      }
      this.loading = false
      if (this.isAuthenticated)
      {
        if (localStorage.getItem('accessToken') == null) {
          this.accessToken = await this.auth0Client.getTokenSilently()
          this.user = await this.auth0Client.getUser()
        } else {
          this.accessToken = localStorage.getItem('accessToken')
          this.user = JSON.parse(localStorage.getItem('userData'))
        }
        const axios = require('axios').default
        axios.defaults.headers.common.Authorization = 'Bearer ' + this.accessToken
        if (this.user && this.user.email)
        {
          await options.store.dispatch('updateEmail', this.user.email)
        }

        await options.store.dispatch('classificators/fetchClassificators')
        await options.store.dispatch('filters/fetchFilters')
      }
    }
  })

  return instance
}

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options)
  {
    Vue.prototype.$auth = useAuth0(options)
  }
}
